
import { Vue, Component, Watch, Prop } from "vue-property-decorator";
import { validate as uuidValidate } from "uuid";
import { globalStore, uiStore } from "@/store/store";
import {
  addSelectorToStore,
  createSelectorFromName,
  getClassNameElementSlug,
  getSuggestedClassNameForElement,
  setElementSelectors
} from "@/oxygen/classes/lib/classes";
import { isValidClassName } from "@/lib/selectors/selectors";
import NiceButton from "@/components/UI/NiceButton.vue";
import Icon from "@/components/UI/Icon.vue";
import OxyClassToken from "@/oxygen/classes/OxyClassToken.vue";
import OxygenCopyPastContextMenu from "@/oxygen/properties-panel/OxygenCopyPastContextMenu.vue";
import OxyClassBreakpointIcon from "@/oxygen/classes/OxyClassBreakpointIcon.vue";

@Component({
  components: {
    OxyClassBreakpointIcon,
    OxygenCopyPastContextMenu,
    Icon,
    NiceButton,
    OxyClassToken
  }
})
export default class ClassDropdown extends Vue {
  @Prop()
  tab!: string;

  newClassName: string | null = null;
  errorMessage: string | null = null;

  @Watch("newClassName")
  onNewClassNameChange() {
    // Reset error message when user starts typing
    if (this.newClassName) {
      this.errorMessage = null;
    }
  }

  @Watch("elementId", { immediate: true })
  onElementIdChange() {
    if (this.activeSelector) {
      uiStore.setCurrentElementTab("classes");
      uiStore.setCurrentElementOxyNestedSelector(undefined);
    }
  }

  @Watch("tab", { immediate: true })
  onTabChange() {
    if (this.tab !== "classes") return;
    if (this.activeSelector) return;

    const firstUnlockedSelector = this.elementClasses.find(e => {
      const selector = this.availableClasses.find(c => c.id == e);
      return selector && !selector.locked;
    });

    if (firstUnlockedSelector) {
      this.activateSelector(firstUnlockedSelector);
    }
  }

  get element() {
    return uiStore.activeElement;
  }

  get elementId() {
    return uiStore.activeElementId;
  }

  get availableClasses() {
    return globalStore.oxySelectors.filter(e => e.type === "class");
  }

  get activeSelector() {
    return uiStore.activeOxySelector;
  }

  get activeNestedSelector() {
    return uiStore.activeOxyNestedSelector;
  }

  get elementClasses(): string[] {
    if (!this.element) return [];
    return this.element?.data?.properties?.meta?.classes || [];
  }

  get showSuggestClassName() {
    const existingClassNames = this.elementClasses
      .map(e => this.availableClasses.find(c => c.id == e))
      .map(e => e?.name)
      .filter(e => e);

    const alreadyContainsASimilarClass = existingClassNames.some(e =>
      e?.match(new RegExp(`^${this.elementSlug}-\\d+$`))
    );

    return !this.newClassName && !alreadyContainsASimilarClass;
  }

  get elementSlug() {
    if (!this.element) return "";
    return getClassNameElementSlug(this.element);
  }

  get suggestClassName() {
    if (!this.element) return "";
    return getSuggestedClassNameForElement(this.element);
  }

  addSuggestedClass() {
    this.updateClasses([...this.elementClasses, this.suggestClassName]);
  }

  createClassIfNeeded(className: string) {
    const foundClass = this.availableClasses.find(e => e.name == className);

    if (foundClass) return foundClass;

    const cssClass = createSelectorFromName(className);

    addSelectorToStore(cssClass);

    return cssClass;
  }

  createFromNameAndSelect() {
    if (!this.newClassName) return;
    const cssClass = this.createClassIfNeeded(this.newClassName);
    this.activateSelector(cssClass.id);
  }

  updateClasses(classes?: string[]) {
    if (!this.elementId) return;

    if (!classes) {
      setElementSelectors(this.elementId, []);
      return;
    }

    this.errorMessage = null;

    const newClassName = classes.find(e => !uuidValidate(e));
    const existingClasses = classes.filter(e => uuidValidate(e));

    if (newClassName) {
      if (!isValidClassName(`.${newClassName}`)) {
        // Reset the input value to the previous state as v-combobox mutates the value
        setElementSelectors(this.elementId, existingClasses);
        this.errorMessage = "Invalid class name";
        return;
      }

      const classId = this.createClassIfNeeded(newClassName).id;

      if (!this.elementClasses.includes(classId)) {
        existingClasses.push(classId);
      }
    }

    setElementSelectors(this.elementId, existingClasses);

    this.activateSelector(existingClasses[existingClasses.length - 1]);
  }

  activateSelector(id: string) {
    if (!this.elementId) return;

    uiStore.setCurrentElementOxySelector(id);
    uiStore.setCurrentElementTab("classes");

    this.closeMenu();
  }

  clearSelector() {
    uiStore.setCurrentElementOxySelector(undefined);
    uiStore.setCurrentElementTab(undefined);
  }

  selectNestedSelector(id: string) {
    uiStore.setCurrentElementOxyNestedSelector(id);
  }

  clearNestedSelector() {
    uiStore.setCurrentElementOxyNestedSelector(undefined);
  }

  closeMenu() {
    if (!this.$refs.input) return;

    // @ts-ignore
    this.$refs.input.isMenuActive = false;
    // @ts-ignore
    this.$refs.input.blur();
  }

  onBlur(event: MouseEvent) {
    const target = event.relatedTarget as HTMLElement;

    if (target?.classList.contains("v-list-item")) return;

    this.newClassName = "";
  }

  getNestedSelectorForActiveSelector(selectorId: string) {
    return selectorId === this.activeSelector?.id
      ? this.activeNestedSelector
      : undefined;
  }

  draggableItems: {
    node: HTMLElement;
    rect: DOMRect;
  }[] = [];

  draggingId: string | null = null;
  draggedOverId: string | null = null;
  dropPosition: string | null = null;
  draggingFromIndex: number | null = null;
  dropIndex: number | null = null;

  containerRect: DOMRect | null = null;

  dragStart(event: DragEvent, draggingId: string) {
    this.draggingId = draggingId;

    // @ts-ignore
    event.dataTransfer.dropEffect = "move";

    this.activateSelector(draggingId);

    document.addEventListener("dragover", this.dragOver, false);

    const container = this.$refs.container as HTMLElement;
    const items = container.querySelectorAll(".oxy-class-token");

    this.containerRect = container.getBoundingClientRect();
    this.draggableItems = Array.from(items).map(element => ({
      node: element as HTMLElement,
      rect: element.getBoundingClientRect()
    }));
  }

  dragOver(event: DragEvent) {
    event.preventDefault();
    // @ts-ignore
    event.dataTransfer.dropEffect = "move";
  }

  dragMove(event: DragEvent) {
    if (!event.clientX && !event.clientY) return;

    const closestItem = this.findClosestItem(event.clientX, event.clientY);

    this.draggedOverId = closestItem.node?.dataset.id || null;
    this.dropPosition = closestItem.position || null;

    if (this.draggedOverId && this.draggingId) {
      const draggedOverIndex = this.elementClasses.indexOf(this.draggedOverId);

      this.dropIndex =
        this.dropPosition == "before"
          ? draggedOverIndex
          : Math.min(draggedOverIndex + 1, this.elementClasses.length);

      this.draggingFromIndex = this.elementClasses.indexOf(this.draggingId);

      if (
        this.dropIndex === this.draggingFromIndex ||
        this.dropIndex === this.draggingFromIndex + 1 ||
        this.draggedOverId === this.draggingId
      ) {
        this.draggedOverId = null;
        this.dropPosition = null;
      }
    }
  }

  dragEnd() {
    if (this.draggedOverId === null) return;
    if (this.draggingFromIndex === null) return;
    if (this.dropIndex === null) return;

    this.moveItem(this.draggingFromIndex, this.dropIndex);
    this.clearDragStyles();

    document.removeEventListener("dragover", this.dragOver, false);
  }

  clearDragStyles() {
    this.draggedOverId = null;
    this.dropPosition = null;
    this.draggingId = null;
    this.draggingFromIndex = null;
    this.dropIndex = null;
  }

  moveItem(fromIndex: number, toIndex: number) {
    if (!this.elementId) return;

    const array = this.elementClasses;

    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex > array.length ||
      toIndex > array.length
    ) {
      return;
    }

    const [item] = array.splice(fromIndex, 1);

    if (toIndex > fromIndex) {
      toIndex--;
    }

    array.splice(toIndex, 0, item);

    setElementSelectors(this.elementId, array);
  }

  findClosestItem(clientX: number, clientY: number) {
    let closestItem: {
      node: HTMLElement | null;
      rect: DOMRect | null;
    } = {
      node: null,
      rect: null
    };

    let minDistance = Infinity;
    let position = "";

    this.draggableItems.forEach(item => {
      const rectCenterX = item.rect.left + item.rect.width / 2;
      const rectCenterY = item.rect.top + item.rect.height / 2;

      const distance = Math.sqrt(
        Math.pow(clientX - rectCenterX, 2) + Math.pow(clientY - rectCenterY, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestItem = item;

        position = clientX < rectCenterX ? "before" : "after";
      }
    });

    const containerY = this.containerRect!.top + this.containerRect!.height;

    if (containerY - clientY < -50) {
      position = "after";
      closestItem = this.draggableItems[this.draggableItems.length - 1];
    }

    if (containerY - clientY > 100) {
      position = "before";
      closestItem = this.draggableItems[0];
    }

    return {
      position,
      ...closestItem
    };
  }
}
